import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin, Newspaper } from "lucide-react";
import { BRAND, FOUNDERS, STATS } from "../mock/mock";

const PageHero = ({ eyebrow, title, italic, sub }) => (
  <section className="relative bg-black text-white pt-40 pb-20 md:pt-48 md:pb-32">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10">
      <p className="text-[11px] uppercase tracking-[0.32em] text-white/60 mb-6">{eyebrow}</p>
      <h1 className="font-display text-[14vw] md:text-[8vw] uppercase leading-[0.92]">
        {title} <span className="font-serif-display italic font-black">{italic}</span>
      </h1>
      {sub && (
        <p className="mt-8 max-w-2xl text-white/75 text-lg leading-relaxed">
          {sub}
        </p>
      )}
    </div>
  </section>
);

const About = () => {
  return (
    <main>
      <PageHero
        eyebrow="Who we are"
        title="Built for the"
        italic="real world."
        sub="Adways Marketing Solution is a Pan-India Out-of-Home advertising company. We pair premium media with bold creative — turning streets, transit and skylines into stages for India's most ambitious brands."
      />

      {/* Mission */}
      <section className="bg-white text-black">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="text-[11px] uppercase tracking-[0.32em] text-black/55 mb-6">Our Mission</p>
            <h2 className="font-display text-[10vw] md:text-[5vw] uppercase leading-[0.92]">
              Make brands <br />
              <span className="font-serif-display italic font-black">unmissable.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-black/75 text-lg leading-relaxed max-w-2xl">
            <p>
              AMS was founded on a simple belief: the best advertising lives where
              people already are. On the way to work. Outside the cinema. Across
              from the cafe. In the corner of every commute.
            </p>
            <p>
              We build campaigns that respect attention — with prime locations,
              precise creative and execution you can trust. From Mumbai to
              Guwahati, our network is built to put your brand exactly where it
              needs to be.
            </p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-neutral-50 text-black border-y border-black/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 grid grid-cols-2 md:grid-cols-4 gap-y-10">
          {STATS.map((s) => (
            <div key={s.label} className="border-l border-black/15 pl-6 first:border-l-0 first:pl-0">
              <div className="font-display text-5xl md:text-6xl">{s.value}</div>
              <div className="text-[11px] uppercase tracking-[0.22em] mt-3">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Founders detailed */}
      <section className="bg-white text-black">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:items-end mb-14">
            <div className="md:col-span-8">
              <p className="text-[11px] uppercase tracking-[0.32em] text-black/55 mb-6">Leadership</p>
              <h3 className="font-display text-[12vw] md:text-[5.2vw] uppercase leading-[0.92]">
                The two who <span className="font-serif-display italic font-black">started</span> AMS.
              </h3>
            </div>
            <p className="md:col-span-4 max-w-md text-black/65 leading-relaxed">
              Founders & operators — building India's most ambitious outdoor
              advertising company from the ground up.
            </p>
          </div>

          {/* Group photo */}
          <div className="relative aspect-[16/9] md:aspect-[21/9] w-full overflow-hidden grain mb-12 md:mb-16">
            <img
              src={BRAND.foundersPhoto}
              alt="AMS Founders"
              className="w-full h-full object-cover grayscale"
            />
            <div className="absolute bottom-5 left-5 text-white text-[11px] uppercase tracking-[0.24em] inline-flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5" /> Founders · AMS
            </div>
          </div>

          {/* Side-by-side bios */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14">
            {FOUNDERS.map((f, i) => (
              <article key={f.name} className="border-t border-black/15 pt-8">
                <div className="flex items-baseline justify-between gap-4 mb-3">
                  <span className="text-[11px] uppercase tracking-[0.3em] text-black/45">
                    0{i + 1}
                  </span>
                  <span className="text-[11px] uppercase tracking-[0.24em] text-black/55">
                    {f.role}
                  </span>
                </div>
                <h4 className="font-display text-5xl md:text-6xl uppercase leading-[0.95]">
                  {f.name}
                </h4>
                <div className="mt-6 space-y-4 text-black/70 leading-relaxed text-[15px]">
                  {f.bio.split("\n\n").map((para) => (
                    <p key={`${f.name}-${para.slice(0, 24)}`}>{para}</p>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Press Release */}
      <section className="bg-neutral-50 text-black border-t border-black/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-4">
              <p className="text-[11px] uppercase tracking-[0.32em] text-black/55 mb-6">
                Press
              </p>
              <h2 className="font-display text-5xl md:text-6xl uppercase leading-[0.92]">
                As seen <br />
                <span className="font-serif-display italic font-black">in.</span>
              </h2>
            </div>

            <a
              href="https://techbullion.com/how-two-22-year-old-entrepreneurs-are-redefining-delhi-ncrs-ooh-industry/"
              target="_blank"
              rel="noopener noreferrer"
              className="lg:col-span-8 group block bg-white border border-black/15 hover:border-black transition-colors duration-200"
            >
              <article className="p-8 md:p-10 flex flex-col md:flex-row gap-8 md:items-center">
                <div className="flex items-center gap-3 shrink-0">
                  <span className="w-12 h-12 rounded-full border border-black inline-flex items-center justify-center">
                    <Newspaper className="w-5 h-5" />
                  </span>
                  <div className="flex flex-col">
                    <span className="text-[10px] uppercase tracking-[0.3em] text-black/55">
                      Featured in
                    </span>
                    <span className="font-display uppercase text-xl tracking-wider">
                      TechBullion
                    </span>
                  </div>
                </div>

                <div className="md:border-l md:border-black/15 md:pl-8 flex-1">
                  <p className="text-[11px] uppercase tracking-[0.24em] text-black/55 mb-3">
                    The New Generation of Outdoor Advertising
                  </p>
                  <h3 className="font-serif-display text-2xl md:text-3xl leading-[1.2] font-black">
                    How Two 22-Year-Old Entrepreneurs Are Redefining Delhi NCR's
                    OOH Industry
                  </h3>
                  <p className="mt-4 text-black/65 text-sm md:text-base leading-relaxed line-clamp-3 max-w-2xl">
                    The story of Adways Marketing Solutions — founded by Sidhanth
                    Bhutani and Om Malhotra — and how they're disrupting one of
                    India's most relationship-driven industries with innovation,
                    speed and premium media.
                  </p>
                </div>

                <div className="shrink-0 self-start md:self-center">
                  <span className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] font-semibold px-5 py-3 border border-black group-hover:bg-black group-hover:text-white transition-colors duration-200">
                    Read Article
                    <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform duration-300" />
                  </span>
                </div>
              </article>
            </a>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-black text-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28 flex flex-col md:flex-row md:items-end md:justify-between gap-10">
          <h3 className="font-display text-[10vw] md:text-[5.6vw] uppercase leading-[0.92]">
            Work with <span className="font-serif-display italic font-black">us.</span>
          </h3>
          <Link
            to="/contact"
            className="inline-flex items-center gap-3 text-sm uppercase tracking-[0.22em] font-semibold px-7 py-4 border border-white hover:bg-white hover:text-black transition-colors duration-200"
          >
            Start a Campaign <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </main>
  );
};

export default About;
